import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { InputMask } from "primereact/inputmask";
import {
  FETCH_TEMPLATE_DATA,
  REMOVE_SIGNATURE,
} from "../redux/actions/actionTypes";
import Layout from "../components/Layout";
import { Document, Page } from "react-pdf";
import CustomInput from "../components/Input";
import CustomButton from "../components/Button";
import { PDFDocument, rgb } from "pdf-lib";
import { showErrorToast } from "../utils/toast";
import CollabSignature from "../components/CollabSignature";
import SignatureModal from "../components/SignatureModal";
import { serverBaseUrl } from "../config/envs";
import axios from "axios";
import ThankYouPage from "./thank_you";
import AlreadySigned from "./already_signed";
import moment from "moment";

const TemplateSignPage = () => {
  const dispatch = useDispatch();
  const [pdfFile, setPdfFile] = useState(null);
  const { signSecretId } = useParams();
  const [signImage, setSignImage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const signerData = useSelector((state) => state?.signature?.signerData);
  const [numPages, setNumPages] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [selectedFillId, setSelectedFillId] = useState("");
  const [signBtnLoader, setSignBtnLoader] = useState(false);
  const [filledSignatureIds, setFilledSignatureIds] = useState([]);
  const [thankYou, setThankYou] = useState(false);
  const { signerId } = useParams();
  const [pageSizes, setPageSizes] = useState([]);

  const currentSigner = signerData?.data?.signLink?.signers?.find(
    (signer) => signer.is_signed === 0
  );
  const currentSignerId = currentSigner ? currentSigner.signing_order : null;

  const currentSignerFields = signerData?.data?.signLink?.templateField?.filter(
    (field) =>
      field.signing_order === currentSignerId &&
      field.field_type !== "signature" &&
      field.field_type !== "gender" &&
      field.field_type !== "date" &&
      field.field_type !== "phone"
  );

  const getSignatureField = signerData?.data?.signLink?.templateField?.filter(
    (field) =>
      field.signing_order === currentSignerId &&
      field.field_type === "signature"
  );

  const getGenderFields = signerData?.data?.signLink?.templateField?.filter(
    (field) =>
      field.signing_order === currentSignerId && field.field_type === "gender"
  );

  const getDateFields = signerData?.data?.signLink?.templateField?.filter(
    (field) =>
      field.signing_order === currentSignerId && field.field_type === "date"
  );

  const getPhoneFields = signerData?.data?.signLink?.templateField?.filter(
    (field) =>
      field.signing_order === currentSignerId && field.field_type === "phone"
  );

  const noFields =
    currentSignerFields?.length === 0 &&
    getSignatureField?.length === 0 &&
    getGenderFields?.length === 0 &&
    getDateFields?.length === 0 &&
    getPhoneFields?.length === 0;

  const currentPdfUrl =
    signerData?.data?.signLink?.signers[0]?.is_signed === 2
      ? signerData?.data?.signLink?.final_document_url
      : signerData?.data?.signLink?.template_docs?.link;

  const alreadySigned =
    signerData?.data?.signLink?.signers?.find(
      (signer) => signer.id === parseInt(signerId)
    )?.is_signed === 2;

  useEffect(() => {
    const fetchPdf = async () => {
      if (currentPdfUrl) {
        try {
          const response = await fetch(currentPdfUrl);
          const arrayBuffer = await response.arrayBuffer();
          setPdfFile(arrayBuffer);

          const pdfDoc = await PDFDocument.load(arrayBuffer, {
            ignoreEncryption: true,
          });
          const allPageSizes = pdfDoc.getPages().map((page) => page.getSize());
          setPageSizes(allPageSizes);
        } catch (error) {
          console.error("Error loading PDF:", error);
        }
      }
    };

    fetchPdf();
  }, [currentPdfUrl]);

  const signerIds = useMemo(() => {
    return getSignatureField
      ?.sort((a, b) => a.page - b.page)
      ?.map((element) => `signature_${element.id}`);
  }, [getSignatureField]);

  const renderedDocument = useMemo(
    () => (
      <Document
        file={{ data: pdfFile }}
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages);
          setPdfLoaded(true);
        }}
      >
        {Array.from(new Array(numPages), (_, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            width={600}
            className="mb-4"
          />
        ))}
      </Document>
    ),
    [pdfFile, numPages]
  );

  const uploadGeneratedPdf = useCallback(
    async (file) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload-sign-template-document`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );
        if (uploadResponse.data?.data?.url) {
          const response = await axios.post(
            `${serverBaseUrl}/submit_template_sign`,
            {
              sign_secret_id: signerData?.data?.signLink?.sign_secret_id,
              final_document_url: uploadResponse.data?.data?.url,
              template_id: currentSigner?.template_id,
              signer_id: currentSigner?.id,
              date_signed: new Date(),
            },
            {
              headers: {
                "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
              },
            }
          );

          if (response.data.success) {
            setThankYou(true);
          }
        }
      } catch (err) {
        setIsLoading(false);
      }
    },
    [
      signerData?.data?.signLink?.sign_secret_id,
      currentSigner?.template_id,
      currentSigner?.id,
    ]
  );

  const validateAllFields = () => {
    const areTextFieldsFilled = currentSignerFields?.every((field, i) => {
      const fieldValue = formValues[`${field.field_name}_${i}`];
      return fieldValue && fieldValue.trim() !== "";
    });

    if (!areTextFieldsFilled) {
      showErrorToast("Please fill in all text fields.");
      return false;
    }

    // const today = moment();
    // const areDateFieldsValid = getDateFields?.every((field, i) => {
    //   const uniqueKey = `Date_${i}`;
    //   const dateValue = formValues[uniqueKey];
    //   if (!dateValue) {
    //     showErrorToast("Please fill in all date fields.");
    //     return false;
    //   }
    //   const date = moment(dateValue, "MM/DD/YYYY", true);
    //   if (!date.isValid()) {
    //     showErrorToast("Invalid date format. Please use MM/DD/YYYY.");
    //     return false;
    //   }
    //   if (date.isAfter(today)) {
    //     showErrorToast("Date cannot be in the future.");
    //     return false;
    //   }
    //   return true;
    // });

    // if (!areDateFieldsValid) {
    //   return false;
    // }

    const arePhoneFieldsValid = getPhoneFields?.every((field, i) => {
      const uniqueKey = `Phone_${i}`;
      const phoneValue = formValues[uniqueKey];
      if (!phoneValue || phoneValue.replace(/\D/g, "").length !== 10) {
        showErrorToast("Please enter a valid phone number.");
        return false;
      }
      return true;
    });

    if (!arePhoneFieldsValid) {
      return false;
    }

    const areGenderFieldsFilled = getGenderFields?.every((field, i) => {
      const uniqueKey = `Gender_${i}`;
      const genderValue = formValues[uniqueKey];
      if (!genderValue) {
        showErrorToast("Please select a gender.");
        return false;
      }
      return true;
    });

    if (!areGenderFieldsFilled) {
      return false;
    }

    const areSignaturesFilled = getSignatureField?.every((_, i) =>
      filledSignatureIds.includes(`signature_${i}`)
    );

    if (!areSignaturesFilled) {
      showErrorToast("Please sign all signature fields.");
      return false;
    }

    return true;
  };

  const generateModifiedPdf = async () => {
    if (!pdfFile) return false;
    if (!validateAllFields()) {
      return;
    }
    try {
      const pdfArrayBuffer = pdfFile;
      const existingPdf = await PDFDocument.load(pdfArrayBuffer);
      const pages = existingPdf.getPages();

      if (signImage) {
        const imageResponse = await fetch(signImage);
        const imageArrayBuffer = await imageResponse.arrayBuffer();
        const contentType = imageResponse.headers.get("Content-Type");

        let image;
        if (contentType === "image/png") {
          image = await existingPdf.embedPng(imageArrayBuffer);
        } else if (contentType === "image/jpeg") {
          image = await existingPdf.embedJpg(imageArrayBuffer);
        } else {
          throw new Error("Unsupported image type");
        }
        getSignatureField.forEach(
          ({ page_number, position_x, position_y, width, height }) => {
            const pageIndex = page_number - 1;
            if (pageIndex >= 0 && pageIndex < pages.length) {
              const page = pages[pageIndex];
              const { height: pageHeight } = page.getSize();

              page.drawImage(image, {
                x: position_x + 20,
                y: pageHeight - position_y - height,
                width: 200,
                height: 60,
              });
            }
          }
        );
      }

      currentSignerFields.forEach(
        (
          { height, width, page_number, position_x, position_y, field_name },
          i
        ) => {
          const pageIndex = page_number - 1;
          if (pageIndex >= 0 && pageIndex < pages.length) {
            const page = pages[pageIndex];
            const { height: pageHeight } = page.getSize();

            const textValue = formValues[`${field_name}_${i}`] || "";
            if (textValue) {
              page.drawText(textValue, {
                x: position_x + 20,
                y: pageHeight - position_y - height,
                size: 12,
                color: rgb(0, 0, 0),
                maxWidth: width,
              });
            }
          }
        }
      );

      getGenderFields.forEach(
        (
          { height, width, page_number, position_x, position_y, field_name },
          i
        ) => {
          const pageIndex = page_number - 1;
          if (pageIndex >= 0 && pageIndex < pages.length) {
            const page = pages[pageIndex];
            const { height: pageHeight } = page.getSize();

            const textValue = formValues[`${field_name}_${i}`] || "";
            if (textValue) {
              page.drawText(textValue, {
                x: position_x + 20,
                y: pageHeight - position_y - height,
                size: 12,
                color: rgb(0, 0, 0),
                maxWidth: width,
              });
            }
          }
        }
      );

      getDateFields.forEach(
        ({ height, width, page_number, position_x, position_y }, i) => {
          const pageIndex = page_number - 1;
          if (pageIndex >= 0 && pageIndex < pages.length) {
            const page = pages[pageIndex];
            const { height: pageHeight } = page.getSize();

            const textValue =
              moment(signerData?.data?.signLink?.date_created).format(
                "MM/DD/YYYY"
              ) || "";
            if (textValue) {
              page.drawText(textValue, {
                x: position_x + 20,
                y: pageHeight - position_y - height,
                size: 12,
                color: rgb(0, 0, 0),
                maxWidth: width,
              });
            }
          }
        }
      );

      getPhoneFields.forEach(
        (
          { height, width, page_number, position_x, position_y, field_name },
          i
        ) => {
          const pageIndex = page_number - 1;
          if (pageIndex >= 0 && pageIndex < pages.length) {
            const page = pages[pageIndex];
            const { height: pageHeight } = page.getSize();

            const textValue = formValues[`${field_name}_${i}`] || "";
            if (textValue) {
              page.drawText(textValue, {
                x: position_x + 20,
                y: pageHeight - position_y - height,
                size: 12,
                color: rgb(0, 0, 0),
                maxWidth: width,
              });
            }
          }
        }
      );

      const modifiedPdf = await existingPdf.save();
      const blob = new Blob([modifiedPdf], { type: "application/pdf" });
      await uploadGeneratedPdf(blob);
    } catch (error) {
      console.error("Error generating modified PDF:", error);
      return false;
    }
  };

  const handleOnSignatureClick = useCallback(
    ({ fillId }) => {
      if (filledSignatureIds.includes(fillId)) return;

      setSignBtnLoader(true);
      setSelectedFillId(fillId);
      if (signImage) {
        setTimeout(() => {
          setFilledSignatureIds((prev) => [...prev, fillId]);
          setSignBtnLoader(false);
        }, 500);
      } else {
        setIsOpen(true);
        setSignBtnLoader(false);
      }
    },
    [signImage, filledSignatureIds]
  );

  const getAdminSignature = useCallback(async () => {
    if (currentSigner?.id) {
      try {
        const response = await axios.get(
          `${serverBaseUrl}/get_signature?signerId=${currentSigner?.id}`,
          {
            headers: {
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );

        if (response?.data?.data[0]?.url) {
          setSignImage(response?.data?.data[0]?.url);

          if (signerData?.data?.signLink?.status === 1) {
            setFilledSignatureIds((prev) => [...prev]);
          }
        } else {
          setSignImage(null);
        }
      } catch (error) {
        setSignImage(null);
      }
    }
  }, [
    currentSigner?.id,
    signerData?.data?.signLink?.sign_secret_id,
    signerData?.data?.signLink?.status,
  ]);

  const onSignatureSubmit = async (newImage, fillId) => {
    setSignImage(newImage);
    setFilledSignatureIds((prev) => [...prev, fillId]);
    await getAdminSignature();
    setIsLoading(false);
    setIsOpen(false);
  };

  const uploadSignatureFile = useCallback(
    async (file, fillId) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
            },
          }
        );
        if (uploadResponse.data?.data?.url) {
          if (signImage != null) {
            const response = await axios.put(
              `${serverBaseUrl}/update_signature`,
              {
                signerId: currentSigner?.id,
                clinicId: signerData?.data?.signLink?.qualiphy_clinic_id || 0,
                url: uploadResponse.data?.data?.url,
                path: uploadResponse.data?.data?.url.split("/").pop(),
              },
              {
                headers: {
                  "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
                },
              }
            );
            if (response?.data?.success) {
              onSignatureSubmit(uploadResponse.data?.data?.url, fillId);
            } else {
              showErrorToast(
                "Something went wrong while uploading signature, please try again"
              );
              setIsLoading(false);
            }
            return;
          }
          const response = await axios.post(
            `${serverBaseUrl}/create_signature`,
            {
              signerId: currentSigner?.id,
              clinicId: signerData?.data?.signLink?.qualiphy_clinic_id,
              url: uploadResponse.data?.data?.url,
              path: uploadResponse.data?.data?.url.split("/").pop(),
            },
            {
              headers: {
                "sign-secret-id": signerData?.data?.signLink?.sign_secret_id,
              },
            }
          );
          if (response?.data?.success) {
            onSignatureSubmit(uploadResponse.data?.data?.url, fillId);
          } else {
            showErrorToast(
              "Something went wrong while uploading signature, please try again"
            );
            setIsLoading(false);
          }
        }
      } catch (err) {
        setIsLoading(false);
      }
    },
    [
      currentSigner?.id,
      signImage,
      signerData?.data?.signLink?.qualiphy_clinic_id,
      signerData?.data?.signLink?.sign_secret_id,
    ]
  );

  useEffect(() => {
    getAdminSignature();
  }, [getAdminSignature]);

  const removeSignature = async (file) => {
    let _payLoad = {
      signerId: currentSigner?.id,
      clinicId: signerData?.data?.signLink?.qualiphy_clinic_id || 0,
      signSecretId: signerData?.data?.signLink?.sign_secret_id,
    };
    dispatch({
      type: REMOVE_SIGNATURE,
      payload: _payLoad,
    });
    setSignImage(null);
    setFilledSignatureIds([]);
  };

  const handleOnSignatureChange = useCallback(
    ({ fillId }) => {
      setIsOpen(true);
      setSelectedFillId(fillId);
      setFilledSignatureIds((prev) => [...prev, fillId]);
    },
    [setIsOpen]
  );

  const handleInputChange = useCallback((fieldName, value, fieldType) => {
    setFormValues((prevValues) => {
      return { ...prevValues, [fieldName]: value };
    });
  }, []);

  const allFieldsFilled =
    currentSignerFields?.every(
      (field, i) => formValues[`${field.field_name}_${i}`]?.trim() !== ""
    ) ?? false;

  const allSignaturesFilled =
    getSignatureField?.every((_, i) =>
      filledSignatureIds.includes(`signature_${i}`)
    ) ?? false;

  useEffect(() => {
    dispatch({
      type: FETCH_TEMPLATE_DATA,
      payload: signSecretId,
    });
  }, [signSecretId, dispatch]);

  if (thankYou) {
    return (
      <ThankYouPage
        title={
          noFields
            ? "Thank you for reviewing the document"
            : "Thank you for signing the document"
        }
        description={
          noFields
            ? "Your document has been reviewed"
            : "Your document has been successfully submitted"
        }
      />
    );
  }

  if (alreadySigned) {
    return <AlreadySigned />;
  }

  return (
    <Layout>
      <div className="relative flex flex-col w-[600px] gap-4 overflow-x-hidden mx-auto items-center justify-start min-h-screen px-4 sm:px-6 lg:px-8 overflow-y-auto bg-gray-50">
        <div className="relative">{renderedDocument}</div>

        {pdfLoaded && pageSizes?.length > 0 && (
          <div className="absolute top-0 left-0 w-full pointer-events-none">
            {currentSignerFields?.map((field, i) => {
              const uniqueKey = `${field.field_name}_${i}`;
              const uniqueName = uniqueKey;

              return (
                <div
                  key={uniqueKey}
                  className="absolute pointer-events-auto"
                  style={{
                    left: field.position_x,
                    top:
                      field?.position_y +
                      pageSizes?.[field.page_number - 1]?.height *
                        (field.page_number - 1),
                    width: field.width,
                  }}
                >
                  <CustomInput
                    name={uniqueName}
                    type={field.field_type}
                    placeholder={field.field_name}
                    style={{
                      height: field.height,
                    }}
                    value={
                      field.field_type === "date"
                        ? moment(formValues[uniqueName], "MM/DD/YYYY").format(
                            "YYYY-MM-DD"
                          )
                        : formValues[uniqueName] || ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        uniqueName,
                        e.target.value,
                        field.field_type
                      )
                    }
                  />
                </div>
              );
            })}

            {getGenderFields.length > 0 &&
              getGenderFields.map((gender, i) => {
                const uniqueKey = `${gender.field_name}_${i}`;
                const uniqueName = uniqueKey;
                return (
                  <select
                    placeholder="Select Gender"
                    className="pointer-events-auto border-gray-300"
                    style={{
                      position: "absolute",
                      zIndex: 10,
                      left: gender.position_x,
                      top:
                        gender?.position_y +
                        pageSizes?.[gender.page_number - 1]?.height *
                          (gender.page_number - 1),
                      height: gender.height,
                      width: gender.width,
                    }}
                    onChange={(e) =>
                      handleInputChange(
                        uniqueName,
                        e.target.value,
                        gender.field_type
                      )
                    }
                  >
                    <option value="">Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Prefer not to say</option>
                  </select>
                );
              })}

            {getPhoneFields.length > 0 &&
              getPhoneFields.map((phone, i) => {
                const uniqueKey = `${phone.field_name}_${i}`;
                const uniqueName = uniqueKey;
                return (
                  <InputMask
                    className="pointer-events-auto border-gray-300"
                    style={{
                      position: "absolute",
                      zIndex: 10,
                      left: phone.position_x,
                      top:
                        phone?.position_y +
                        pageSizes?.[phone.page_number - 1]?.height *
                          (phone.page_number - 1),
                      height: phone.height,
                      width: phone.width,
                    }}
                    name={uniqueName}
                    id={uniqueKey}
                    placeholder={"(999) 999-9999"}
                    type="text"
                    value={formValues[uniqueName] || ""}
                    mask={"(999) 999-9999"}
                    slotChar="(999) 999-9999"
                    inputMode="numeric"
                    onChange={(e) =>
                      handleInputChange(
                        uniqueName,
                        e.target.value,
                        phone.field_type
                      )
                    }
                  />
                );
              })}

            {getDateFields.length > 0 &&
              getDateFields.map((date, i) => {
                const uniqueKey = `${date.field_name}_${i}`;
                const uniqueName = uniqueKey;
                return (
                  <div
                    className="border pointer-events-auto border-gray-300 flex items-center"
                    style={{
                      position: "absolute",
                      zIndex: 10,
                      left: date.position_x,
                      top:
                        date?.position_y +
                        pageSizes?.[date.page_number - 1]?.height *
                          (date.page_number - 1),

                      width: date.width,
                    }}
                  >
                    <InputMask
                      className="pointer-events-auto border-none focus:outline-none focus:border-none focus:ring-0"
                      style={{
                        height: date.height,
                        width: date.width - 10,
                      }}
                      name={uniqueName}
                      id={uniqueKey}
                      placeholder={"MM/DD/YYYY"}
                      value={
                        moment(signerData?.data?.signLink?.date_created).format(
                          "MM/DD/YYYY"
                        ) || ""
                      }
                      mask={"99/99/9999"}
                      slotChar="MM/DD/YYYY"
                      disabled
                      inputMode="numeric"
                      onChange={(e) =>
                        handleInputChange(
                          uniqueName,
                          e.target.value,
                          date.field_type
                        )
                      }
                    />
                  </div>
                );
              })}

            {getSignatureField.length > 0 &&
              getSignatureField.map((field, i) => (
                <div
                  className="pointer-events-auto"
                  style={{
                    position: "absolute",
                    zIndex: 10,
                    left: field.position_x,
                    top:
                      field?.position_y +
                      pageSizes?.[field.page_number - 1]?.height *
                        (field.page_number - 1),
                  }}
                >
                  {!signImage ||
                  !filledSignatureIds?.includes(`signature_${i}`) ? (
                    <CustomButton
                      className="pointer-events-auto"
                      key={`signature_${i}`}
                      onClick={() => {
                        handleOnSignatureClick({
                          fillId: `signature_${i}`,
                        });
                      }}
                      label="Sign"
                      labelColor="black"
                      id={`signature_${i}`}
                      isLoading={signBtnLoader}
                    />
                  ) : null}
                  {signImage &&
                  filledSignatureIds?.includes(`signature_${i}`) ? (
                    <div className="max-w-52 max-h-12">
                      <CollabSignature
                        handleOnSignatureClick={() =>
                          handleOnSignatureClick({
                            fillId: `signature_${i}`,
                          })
                        }
                        loading={isLoading}
                        removeSignatures={removeSignature}
                        onChange={() =>
                          handleOnSignatureChange({
                            fillId: `signature_${i}`,
                          })
                        }
                        allButtonIds={[...signerIds, "submit"]}
                        userSignauter={signImage}
                        id={`signature_${i}`}
                      />
                    </div>
                  ) : null}
                </div>
              ))}
          </div>
        )}
        {signerData?.data?.signLink.status !== 2 && (
          <CustomButton
            label={noFields ? "Review" : "Submit"}
            labelColor="black"
            isLoading={isLoading}
            className="mb-4"
            onClick={generateModifiedPdf}
            disabled={!allFieldsFilled || !allSignaturesFilled || isLoading}
          />
        )}
      </div>
      <SignatureModal
        isLoading={isLoading}
        defaultName={currentSigner?.name}
        signatureType={""}
        onSubmit={(signedDoc) => {
          uploadSignatureFile(signedDoc, selectedFillId);
        }}
        onClose={() => setIsOpen(false)}
        open={isOpen}
      />
    </Layout>
  );
};

export default TemplateSignPage;
